import { createDemoSnapshot } from '../domain/seed'
import type { MobileSnapshot } from '../domain/types'
import { deepFreeze } from '../domain/workflow'
import { MemoryStorage, type MobileRepository, type StorageLike } from './repository'

export const MOBILE_DEMO_STORAGE_KEY = 'wash-mobile-demo-v1'

const browserStorage = (): StorageLike => {
  try {
    if (typeof globalThis !== 'undefined' && 'localStorage' in globalThis && globalThis.localStorage) {
      return globalThis.localStorage
    }
  } catch {
    // Privacy mode or an embedded WebView can deny localStorage access.
  }
  return new MemoryStorage()
}

const looksLikeSnapshot = (value: unknown): value is MobileSnapshot => {
  if (!value || typeof value !== 'object') return false
  const candidate = value as Partial<MobileSnapshot>
  return candidate.schemaVersion === '1.0.0'
    && Array.isArray(candidate.members)
    && Array.isArray(candidate.orders)
    && Array.isArray(candidate.evidence)
    && Array.isArray(candidate.events)
    && Array.isArray(candidate.notifications)
    && Array.isArray(candidate.racks)
    && Array.isArray(candidate.reconciliations)
    && Array.isArray(candidate.processedCommandKeys)
    && Boolean(candidate.deviceSettings)
}

/**
 * localStorage-backed demo adapter. No Pinia or view code depends on storage
 * APIs directly, so this class can later be replaced by an HttpRepository.
 */
export class DemoRepository implements MobileRepository {
  constructor(
    private readonly storage: StorageLike = browserStorage(),
    private readonly storageKey = MOBILE_DEMO_STORAGE_KEY,
  ) {}

  async load(): Promise<MobileSnapshot | null> {
    try {
      const raw = this.storage.getItem(this.storageKey)
      if (!raw) return null
      const parsed: unknown = JSON.parse(raw)
      if (!looksLikeSnapshot(parsed)) return null
      return deepFreeze(parsed) as MobileSnapshot
    } catch {
      return null
    }
  }

  async save(snapshot: MobileSnapshot): Promise<void> {
    this.storage.setItem(this.storageKey, JSON.stringify(snapshot))
  }

  async clear(): Promise<void> {
    this.storage.removeItem(this.storageKey)
  }

  async resetToSeed(): Promise<MobileSnapshot> {
    const snapshot = createDemoSnapshot()
    await this.save(snapshot)
    return snapshot
  }
}

